import React, { useState, useEffect, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import { Badge, Modal, ModalBody, ModalHeader } from "reactstrap";
import http from "../../../utils/http-common";
import moment from "moment";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
];

const ReportsList = () => {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState("");
  const [detailModal, setDetailModal] = useState({ open: false, report: null });
  const [updatingId, setUpdatingId] = useState(null);

  const fetchReports = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append("status", statusFilter);
    params.append("page", page);
    params.append("limit", limit);
    http
      .get(`/api/v1/consultations/reports?${params.toString()}`)
      .then((res) => {
        const data = res.data?.data || [];
        const totalCount = res.data?.total ?? res.data?.meta?.total ?? data.length;
        setReports(Array.isArray(data) ? data : []);
        setTotal(totalCount);
      })
      .catch((err) => {
        console.error("Failed to load reports", err);
        setReports([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, limit, statusFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleMarkReviewed = (report) => {
    const id = report._id || report.id;
    if (!id) return;
    setUpdatingId(id);
    http
      .put(`/api/v1/consultations/reports/${id}`, { status: "reviewed" })
      .then(() => {
        fetchReports();
        setDetailModal({ open: false, report: null });
      })
      .catch((err) => {
        console.error("Failed to update report status", err);
      })
      .finally(() => setUpdatingId(null));
  };

  const formatReporter = (report) => {
    const u = report.reportedBy;
    if (!u) return "—";
    if (typeof u === "object") {
      const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
      return name || u.email || u._id || "—";
    }
    return String(u);
  };

  const getStatusBadge = (status) => {
    const s = (status || "pending").toLowerCase();
    const color = s === "reviewed" ? "success" : "warning";
    return <Badge className={`badge-dim badge-${color}`}>{s}</Badge>;
  };

  return (
    <>
      <Head title="Reports" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Reports (Consultation reports)
              </BlockTitle>
              <BlockDes className="text-soft">
                {loading ? "Loading..." : `Total: ${total} report(s).`}
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="mb-3">
            <label className="form-label me-2">Status</label>
            <select
              className="form-select form-select-sm w-auto d-inline-block"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || "all"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <DataTable className="card-stretch" scrollable>
              <DataTableBody compact>
                <DataTableHead>
                  <DataTableRow size="sm"><span className="sub-text">Date</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Consultation</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Type</span></DataTableRow>
                  <DataTableRow size="md"><span className="sub-text">Reporter</span></DataTableRow>
                  <DataTableRow size="sm"><span className="sub-text">Status</span></DataTableRow>
                  <DataTableRow className="nk-tb-col-tools text-right"><span className="sub-text">Actions</span></DataTableRow>
                </DataTableHead>
                {reports.length === 0 ? (
                  <DataTableItem>
                    <DataTableRow colSpan={6} className="text-center text-soft">
                      No reports found.
                    </DataTableRow>
                  </DataTableItem>
                ) : (
                  reports.map((report) => (
                    <DataTableItem key={report._id || report.id}>
                      <DataTableRow size="sm" dataLabel="Date">
                        {report.createdAt ? moment(report.createdAt).format("DD/MM/YY HH:mm") : "—"}
                      </DataTableRow>
                      <DataTableRow size="sm" dataLabel="Consultation">
                        {report.consultation?._id ? String(report.consultation._id).slice(-8) : report.consultation || "—"}
                      </DataTableRow>
                      <DataTableRow size="sm" dataLabel="Type">
                        {report.type || "—"}
                      </DataTableRow>
                      <DataTableRow size="md" dataLabel="Reporter">
                        {formatReporter(report)}
                      </DataTableRow>
                      <DataTableRow size="sm" dataLabel="Status">
                        {getStatusBadge(report.status)}
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools text-right" dataLabel="Actions">
                        <Button
                          color="light"
                          size="sm"
                          className="btn-icon me-1"
                          onClick={() => setDetailModal({ open: true, report })}
                          title="View detail"
                        >
                          <Icon name="eye" />
                        </Button>
                        {report.status === "pending" && (
                          <Button
                            color="primary"
                            size="sm"
                            className="btn-icon"
                            onClick={() => handleMarkReviewed(report)}
                            disabled={updatingId === (report._id || report.id)}
                            title="Mark as reviewed"
                          >
                            {updatingId === (report._id || report.id) ? (
                              <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <Icon name="check" />
                            )}
                          </Button>
                        )}
                      </DataTableRow>
                    </DataTableItem>
                  ))
                )}
              </DataTableBody>
            </DataTable>
          )}

          {!loading && total > limit && (
            <div className="card-inner border-top">
              <PaginationComponent
                itemPerPage={limit}
                totalItems={total}
                currentPage={page}
                paginate={setPage}
              />
            </div>
          )}
        </Block>

        <Modal isOpen={detailModal.open} toggle={() => setDetailModal({ open: false, report: null })} size="md">
          <ModalHeader toggle={() => setDetailModal({ open: false, report: null })}>
            Report detail
          </ModalHeader>
          <ModalBody>
            {detailModal.report && (
              <>
                <p><strong>Date:</strong> {detailModal.report.createdAt ? moment(detailModal.report.createdAt).format("DD/MM/YYYY HH:mm") : "—"}</p>
                <p><strong>Consultation:</strong> {detailModal.report.consultation?._id ?? detailModal.report.consultation ?? "—"}</p>
                <p><strong>Type:</strong> {detailModal.report.type || "—"}</p>
                <p><strong>Reporter:</strong> {formatReporter(detailModal.report)}</p>
                <p><strong>Status:</strong> {getStatusBadge(detailModal.report.status)}</p>
                {detailModal.report.description && (
                  <p><strong>Description:</strong><br /><span className="text-soft">{detailModal.report.description}</span></p>
                )}
                {detailModal.report.status === "pending" && (
                  <Button
                    color="primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => handleMarkReviewed(detailModal.report)}
                    disabled={updatingId === (detailModal.report._id || detailModal.report.id)}
                  >
                    {updatingId === (detailModal.report._id || detailModal.report.id) ? "Updating..." : "Mark as reviewed"}
                  </Button>
                )}
              </>
            )}
          </ModalBody>
        </Modal>
      </Content>
    </>
  );
};

export default ReportsList;
